import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Survey types (matching municipality app structure)
export enum SurveyStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  STOPPED = 'stopped'
}

export interface SurveyTranslation {
  title: string;
  description: string;
}

export interface Survey {
  id: string;
  municipalityId: string;
  status: SurveyStatus;
  translations: Record<string, SurveyTranslation>;
  startDate: Date;
  endDate: Date;
  startDateReadable?: string;
  endDateReadable?: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  responseCount: number;
  uniqueResponseCount: number;
  completionRate: number;
}

interface SurveyStatistics {
  total: number;
  active: number; // in_progress
  draft: number;
  closed: number; // completed
  totalResponses: number;
  averageResponseRate: number;
}

interface SurveyListResponse {
  items: Survey[];
  total: number;
  statistics: SurveyStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to Survey
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): Survey => {
  const docData = data as Record<string, unknown>;
  
  // Convert translations to proper format
  const translationsData = docData.translations as Record<string, { title?: string; description?: string }> | undefined;
  const translations: Record<string, SurveyTranslation> = {};
  if (translationsData) {
    Object.keys(translationsData).forEach((lang) => {
      const trans = translationsData[lang];
      if (trans) {
        translations[lang] = {
          title: trans.title || '',
          description: trans.description || '',
        };
      }
    });
  }

  const startDate = toDate(docData.startDate);
  const endDate = toDate(docData.endDate);

  return {
    id,
    municipalityId,
    status: (docData.status as SurveyStatus) || SurveyStatus.DRAFT,
    translations,
    startDate,
    endDate,
    startDateReadable: docData.startDateReadable as string | undefined,
    endDateReadable: docData.endDateReadable as string | undefined,
    authorId: (docData.authorId as string) || '',
    authorName: (docData.authorName as string) || '',
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    responseCount: (docData.responseCount as number) || 0,
    uniqueResponseCount: (docData.uniqueResponseCount as number) || 0,
    completionRate: (docData.completionRate as number) || 0,
  };
};

export const useMunicipalitySurveys = (municipalityId: string | null) => {
  const [data, setData] = useState<SurveyListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchSurveys = async () => {
      try {
        setLoading(true);
        setError(null);

        const surveysCollection = collection(db, 'municipalities', municipalityId, 'surveys');
        const snapshot = await getDocs(query(surveysCollection, orderBy('createdAt', 'desc')));

        const items: Survey[] = [];
        let totalResponses = 0;
        let activeCount = 0;
        let draftCount = 0;
        let closedCount = 0;

        snapshot.forEach((doc) => {
          const surveyItem = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(surveyItem);

          // Calculate statistics
          totalResponses += surveyItem.responseCount || 0;
          
          // Categorize surveys by status
          if (surveyItem.status === SurveyStatus.IN_PROGRESS) {
            activeCount++;
          } else if (surveyItem.status === SurveyStatus.DRAFT) {
            draftCount++;
          } else if (surveyItem.status === SurveyStatus.COMPLETED) {
            closedCount++;
          }
        });

        const total = items.length;
        const averageResponseRate = total > 0 ? Math.round(totalResponses / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            active: activeCount,
            draft: draftCount,
            closed: closedCount,
            totalResponses,
            averageResponseRate,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality surveys:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality surveys');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, [municipalityId]);

  return { data, loading, error };
};
